import { Box, Drawer, Typography, IconButton, Autocomplete, TextField, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from 'components/Common/Input';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'components/Common/Select';
import { IoClose } from 'react-icons/io5';
import { RiDeleteBin6Line } from 'react-icons/ri';
import {
  useCreateAlternativeName,
  useGetAlternativeName,
  useGetMenuItemModifiers,
  useGetMenuItems,
  useGetMenuOptions,
  useUpdateAlternativeName
} from '@/apis/business/setting/integrations/StreamOrderSettings/hook';
import { useGetIntegration } from '@/apis/business/setting/integrations/hook';
import { useEffect, useState } from 'react';
import AlternativeNameFormSkeleton from './AlternativeNameFormSkeleton';

export default function AlternativeNameForm() {
  const { alternativeNameId, integrationId } = useParams();
  const navigate = useNavigate();
  const { data: IntegrationData } = useGetIntegration(Number(integrationId));
  const locationId = IntegrationData?.locations?.[0]?.id;

  const { data: AlternativeNameData, isLoading } = useGetAlternativeName(
    Number(integrationId),
    Number(alternativeNameId)
  );

  const { control, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: {
      pos_menu_id: '',
      pos_menu_item_id: '',
      alternative_name_for_item: '',
      pos_modifier_ids: [],
      alternative_name_for_modifiers: [{ pos_modifier_id: null, alternative_name: '' }]
    },
    values: {
      pos_menu_id: AlternativeNameData?.pos_menu?.id,
      pos_menu_item_id: AlternativeNameData?.pos_menu_item?.id,
      alternative_name_for_item:
        AlternativeNameData?.pos_menu_item?.alternative_name?.split(',').map((i: string) => i.trim()) ?? [],
      alternative_name_for_modifiers: AlternativeNameData?.pos_modifiers?.length
        ? AlternativeNameData?.pos_modifiers?.map((m: any) => ({
            pos_modifier_id: m.id,
            alternative_name: m.alternative_name
          }))
        : [{ pos_modifier_id: null, alternative_name: '' }]
    }
  });

  const selectedMenuId = Number(watch('pos_menu_id'));
  const selectedItemId = Number(watch('pos_menu_item_id'));

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'alternative_name_for_modifiers'
  });

  const { data: menus, isLoading: loadingMenu } = useGetMenuOptions(locationId);
  const { data: items, isLoading: loadingItems } = useGetMenuItems(locationId, selectedMenuId, {
    source: 'alternative_name',
    record_id: alternativeNameId
  });
  const { data: modifiers, isLoading: loadingModifiers } = useGetMenuItemModifiers(
    locationId,
    selectedMenuId,
    selectedItemId
  );

  const { mutateAsync: createAlternativeName, isPending: isCreating } = useCreateAlternativeName(Number(integrationId));
  const { mutateAsync: updateAlternativeName, isPending: isUpdating } = useUpdateAlternativeName(
    Number(integrationId),
    Number(alternativeNameId)
  );

  const [itemAlternativeNames, setItemAlternativeNames] = useState<string[]>(
    AlternativeNameData?.pos_menu_item?.alternative_name?.split(',').map((item: any) => item.trim()) ?? []
  );

  const onSubmit = (data: any) => {
    if (alternativeNameId) {
      updateAlternativeName(data);
    } else {
      createAlternativeName(data);
    }
  };

  useEffect(() => {
    setItemAlternativeNames(
      AlternativeNameData?.pos_menu_item?.alternative_name?.split(',').map((item: any) => item.trim()) ?? []
    );
  }, [AlternativeNameData?.pos_menu_item?.alternative_name]);

  if (alternativeNameId && isLoading) {
    return <AlternativeNameFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 720 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Header */}
      <Box
        px={3}
        py={2}
        borderBottom='1px solid #DEDEDE'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography variant='h6'>{alternativeNameId ? 'Edit' : 'Add'} Alternative Names</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Grid container spacing={2}>
            {/* Menu */}
            <Grid size={6}>
              <Select
                name='pos_menu_id'
                control={control}
                label='Select Menu'
                fullWidth
                disabled={loadingMenu}
                options={
                  menus?.data?.map((m) => ({
                    label: m.name,
                    value: m.id
                  })) || []
                }
                onChange={(e) => {
                  setValue('pos_menu_id', e?.target?.value);
                  setValue('pos_menu_item_id', '');
                  setValue('alternative_name_for_item', '');
                  setValue('alternative_name_for_modifiers', [{ pos_modifier_id: '', alternative_name: '' }]);
                  setItemAlternativeNames([]);
                }}
                required
              />
            </Grid>

            {/* Item */}
            <Grid size={6}>
              <Select
                name='pos_menu_item_id'
                control={control}
                label='Select Item'
                fullWidth
                disabled={loadingItems}
                options={
                  items?.data?.map((i) => ({
                    label: i.name,
                    value: i.id
                  })) || []
                }
                onChange={(e) => {
                  setValue('pos_menu_item_id', e?.target?.value);
                  setValue('alternative_name_for_item', '');
                  setValue('alternative_name_for_modifiers', [{ pos_modifier_id: '', alternative_name: '' }]);
                  setItemAlternativeNames([]);
                }}
                required
              />
            </Grid>

            {/* Item Alternative Names */}
            {selectedMenuId && selectedItemId ? (
              <Grid size={12}>
                <Controller
                  control={control}
                  name='alternative_name_for_item'
                  render={({ field }) => (
                    <Autocomplete
                      key={AlternativeNameData?.pos_menu_item?.alternative_name?.length}
                      multiple
                      freeSolo
                      options={[]}
                      value={itemAlternativeNames}
                      onChange={(_, newValue) => {
                        setItemAlternativeNames(newValue);
                        field.onChange(newValue?.join(', '));
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key || index}
                              label={option}
                              {...tagProps}
                              size='small'
                              sx={{
                                bgcolor: 'action.selected',
                                '& .MuiChip-label': {
                                  textTransform: 'none'
                                },
                                '& .MuiChip-deleteIcon': {
                                  color: 'text.secondary',
                                  '&:hover': { color: 'text.primary' }
                                }
                              }}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Alternative Names for Item'
                          placeholder='Type and press Enter'
                          fullWidth
                        />
                      )}
                    />
                  )}
                />
                <Typography color='secondary' fontSize={12} mt={1}>
                  You can add multiple alternative names by pressing Enter.
                </Typography>
              </Grid>
            ) : (
              ''
            )}
          </Grid>

          {/* Modifier Alternative Names */}
          {selectedMenuId && selectedItemId ? (
            <Grid border='1px solid #ECECEC' p={2} my={3} borderRadius='4px'>
              <Typography variant='subtitle2' mb={2}>
                Modifier Alternative Names
              </Typography>

              {fields.map((field, index) => (
                <Grid container spacing={2} alignItems='center' key={field.id} mb={1}>
                  <Grid size={5}>
                    <Select
                      name={`alternative_name_for_modifiers.${index}.pos_modifier_id`}
                      control={control}
                      label='Select Modifier'
                      fullWidth
                      disabled={loadingModifiers}
                      options={
                        modifiers?.data?.map((m) => ({
                          label: m.name,
                          value: m.id
                        })) || []
                      }
                    />
                  </Grid>

                  <Grid size={5}>
                    <Input
                      name={`alternative_name_for_modifiers.${index}.alternative_name`}
                      control={control}
                      label='Alternative Name'
                      placeholder='e.g pizza, pie'
                      fullWidth
                      disabled={!watch(`alternative_name_for_modifiers.${index}.pos_modifier_id`)}
                      required={watch(`alternative_name_for_modifiers.${index}.pos_modifier_id`)}
                    />
                  </Grid>

                  <Grid size={2}>
                    {fields.length > 1 && (
                      <IconButton onClick={() => remove(index)} color='error'>
                        <RiDeleteBin6Line />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}

              <Box display='flex' justifyContent='center' mt={2}>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={() => append({ pos_modifier_id: null, alternative_name: '' })}
                >
                  + Add More
                </Button>
              </Box>
            </Grid>
          ) : (
            ''
          )}
        </Box>

        {/* Footer */}
        <Box px={3} py={2} borderTop='1px solid #DEDEDE' display='flex' justifyContent='flex-end' gap={2}>
          <Button variant='outlined' color='secondary' onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isCreating || isUpdating}>
            {alternativeNameId ? 'Save' : 'Add'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
